import { createContext, useContext, useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'leles-customer-journey-v1'

const todayIso = () => new Date().toISOString().slice(0, 10)
const stamp = () => new Date().toISOString()

const initialJourney = () => ({
  order: {
    id: 'OS-2501',
    stage: 'Sin cita',
    financial: 'Sin monto facturable',
    appointment: null,
    customer: { name: 'Cliente del portal', phone: '', email: '', nit: 'CF', address: '' },
    vehicle: { make: '', model: '', year: '', plate: '', color: '', vin: '', mileage: '', fuel: '1/2 tanque' },
    reception: { comments: '', valuables: '', damageNotes: '', photoNames: [], receivedAt: null },
    diagnostic: { summary: '', findings: '', recommendation: '', technician: 'Carlos M.', completedAt: null },
    quote: {
      id: 'C-1101', version: 1, status: 'Sin preparar', promoCode: '', discount: 0,
      lines: [
        { id: 'diagnostic', type: 'Servicio', description: 'Diagnóstico electrónico', quantity: 1, unitPrice: 250, sku: null },
        { id: 'oil', type: 'Material', description: 'Aceite sintético 5W-30 · botella 1L', quantity: 4, unitPrice: 85, sku: 'ACE-5W30' },
        { id: 'filter', type: 'Repuesto', description: 'Filtro de aire', quantity: 1, unitPrice: 175, sku: 'FIL-AIR-01' },
        { id: 'labor', type: 'Mano de obra', description: 'Mantenimiento preventivo', quantity: 1.5, unitPrice: 210, sku: null },
      ],
      sentAt: null, respondedAt: null,
    },
    stockReservedAt: null,
    stockIssuedAt: null,
    startedAt: null,
    finalComments: '',
    finishedAt: null,
    payments: [],
    history: [],
  },
  inventoryLots: [
    { id: 'LT-ACE-A01', sku: 'ACE-5W30', item: 'Aceite sintético 5W-30 · 1L', vendor: 'Lubricantes del Norte', po: 'OC-AX', received: 8, reserved: 0, consumed: 0, unitCost: 52, sentAt: '2026-08-04', deliveredAt: '2026-08-07', acceptedAt: '2026-08-07', warehouse: 'Bodega Zona 5 · A-02' },
    { id: 'LT-ACE-B07', sku: 'ACE-5W30', item: 'Aceite sintético 5W-30 · 1L', vendor: 'Distribuidora MotorPro', po: 'OC-BX', received: 12, reserved: 0, consumed: 0, unitCost: 55, sentAt: '2026-08-18', deliveredAt: '2026-08-24', acceptedAt: '2026-08-25', warehouse: 'Bodega Zona 5 · A-02' },
    { id: 'LT-FIL-114', sku: 'FIL-AIR-01', item: 'Filtro de aire', vendor: 'Repuestos GT, S.A.', po: 'OC-1184', received: 6, reserved: 0, consumed: 0, unitCost: 108, sentAt: '2026-08-20', deliveredAt: '2026-08-22', acceptedAt: '2026-08-22', warehouse: 'Bodega Zona 5 · B-11' },
  ],
  purchaseOrders: [
    { id: 'OC-AX', vendor: 'Lubricantes del Norte', status: 'Recibida', sentAt: '2026-08-04', deliveredAt: '2026-08-07', acceptedAt: '2026-08-07', expectedAt: '2026-08-08', items: '8 × Aceite sintético 5W-30', total: 416 },
    { id: 'OC-BX', vendor: 'Distribuidora MotorPro', status: 'Recibida', sentAt: '2026-08-18', deliveredAt: '2026-08-24', acceptedAt: '2026-08-25', expectedAt: '2026-08-23', items: '12 × Aceite sintético 5W-30', total: 660 },
    { id: 'OC-1184', vendor: 'Repuestos GT, S.A.', status: 'Recibida', sentAt: '2026-08-20', deliveredAt: '2026-08-22', acceptedAt: '2026-08-22', expectedAt: '2026-08-24', items: '6 × Filtro de aire', total: 648 },
    { id: 'OC-1192', vendor: 'Filtros y Más', status: 'En tránsito', sentAt: '2026-08-27', deliveredAt: '', acceptedAt: '', expectedAt: '2026-09-01', items: '10 × Filtro de aceite', total: 740 },
  ],
  stockMovements: [],
})

const JourneyContext = createContext(null)

export const formatMoney = (value) => `Q ${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
export const quoteSubtotal = (order) => order.quote.lines.reduce((sum, line) => sum + Number(line.quantity) * Number(line.unitPrice), 0)
export const orderTotal = (order) => {
  if (['Sin preparar', 'Pendiente de cliente'].includes(order.quote.status)) return 0
  if (order.quote.status === 'No autorizada') return order.quote.promoCode === 'LELES100' ? 0 : 250
  return Math.max(0, quoteSubtotal(order) - Number(order.quote.discount || 0))
}
export const paidTotal = (order) => order.payments.reduce((sum, payment) => sum + Number(payment.amount), 0)
export const orderBalance = (order) => Math.max(0, orderTotal(order) - paidTotal(order))
export const daysBetween = (start, end) => {
  if (!start || !end) return null
  return Math.max(0, Math.round((new Date(`${end}T12:00:00`) - new Date(`${start}T12:00:00`)) / 86400000))
}

const reserveLots = (lots, lines, orderId) => {
  const allocations = []
  const updated = lots.map((lot) => ({ ...lot }))
  lines.filter((line) => line.sku).forEach((line) => {
    let needed = Number(line.quantity)
    updated.filter((lot) => lot.sku === line.sku && lot.acceptedAt).forEach((lot) => {
      if (needed <= 0) return
      const available = lot.received - lot.reserved - lot.consumed
      const quantity = Math.min(available, needed)
      if (quantity > 0) {
        lot.reserved += quantity
        allocations.push({ id: `RES-${orderId}-${lot.id}`, type: 'Reserva', orderId, sku: lot.sku, item: lot.item, lot: lot.id, po: lot.po, vendor: lot.vendor, quantity, at: stamp() })
        needed -= quantity
      }
    })
    if (needed > 0) throw new Error(`Existencia insuficiente para ${line.description}`)
  })
  return { lots: updated, allocations }
}

export function JourneyProvider({ children }) {
  const [data, setData] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || initialJourney() } catch { return initialJourney() }
  })

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)) }, [data])

  const actions = useMemo(() => ({
    scheduleAppointment: ({ date, time, service, contact }) => setData((current) => ({
      ...current,
      order: {
        ...current.order,
        stage: 'Planificada',
        appointment: { date, time, service, source: 'Asistente web', createdAt: stamp() },
        customer: { ...current.order.customer, email: contact.includes('@') ? contact : current.order.customer.email, phone: contact.includes('@') ? current.order.customer.phone : contact },
        history: [...current.order.history, { at: stamp(), event: 'Cita creada desde el asistente web' }],
      },
    })),
    registerReception: (payload) => setData((current) => ({
      ...current,
      order: { ...current.order, ...payload, stage: 'Vehículo ingresado', reception: { ...payload.reception, receivedAt: stamp() }, history: [...current.order.history, { at: stamp(), event: 'Vehículo recibido con evidencia y datos de custodia' }] },
    })),
    saveDiagnostic: (diagnostic) => setData((current) => ({
      ...current,
      order: { ...current.order, stage: 'Diagnosticado', diagnostic: { ...diagnostic, completedAt: stamp() }, history: [...current.order.history, { at: stamp(), event: 'Diagnóstico técnico completado' }] },
    })),
    sendQuote: (lines) => setData((current) => ({
      ...current,
      order: { ...current.order, stage: 'Presupuestado', quote: { ...current.order.quote, lines, status: 'Pendiente de cliente', sentAt: stamp() }, history: [...current.order.history, { at: stamp(), event: `Cotización ${current.order.quote.id} enviada al portal` }] },
    })),
    respondQuote: ({ approved, promoCode = '' }) => setData((current) => {
      const quote = { ...current.order.quote, promoCode: promoCode.trim().toUpperCase(), status: approved ? 'Autorizada' : 'No autorizada', respondedAt: stamp() }
      if (!approved) {
        const waived = quote.promoCode === 'LELES100'
        return { ...current, order: { ...current.order, stage: 'Entregado', financial: waived ? 'Sin monto facturable' : 'Listo para facturar', quote, finishedAt: stamp(), finalComments: 'Cliente no autorizó la reparación. Vehículo disponible para entrega.', history: [...current.order.history, { at: stamp(), event: waived ? 'Cotización rechazada; diagnóstico cubierto por promoción' : 'Cotización rechazada; diagnóstico cobrado al 100%' }] } }
      }
      const reservation = reserveLots(current.inventoryLots, quote.lines, current.order.id)
      return { ...current, inventoryLots: reservation.lots, stockMovements: [...current.stockMovements, ...reservation.allocations], order: { ...current.order, stage: 'Autorizado', financial: 'Listo para facturar', quote, stockReservedAt: stamp(), history: [...current.order.history, { at: stamp(), event: 'Cliente autorizó; materiales reservados por lote y OC' }] } }
    }),
    startRepair: () => setData((current) => {
      const issued = []
      const inventoryLots = current.inventoryLots.map((lot) => {
        if (!lot.reserved) return lot
        issued.push({ id: `SAL-${current.order.id}-${lot.id}`, type: 'Salida', orderId: current.order.id, sku: lot.sku, item: lot.item, lot: lot.id, po: lot.po, vendor: lot.vendor, quantity: lot.reserved, at: stamp() })
        return { ...lot, consumed: lot.consumed + lot.reserved, reserved: 0 }
      })
      return { ...current, inventoryLots, stockMovements: [...current.stockMovements, ...issued], order: { ...current.order, stage: 'En reparación', stockIssuedAt: stamp(), startedAt: stamp(), history: [...current.order.history, { at: stamp(), event: 'Reparación iniciada; salida de bodega registrada por lote' }] } }
    }),
    finishOrder: (comments) => setData((current) => ({
      ...current,
      order: { ...current.order, stage: 'Entregado', financial: paidTotal(current.order) >= orderTotal(current.order) ? 'Pagado' : paidTotal(current.order) > 0 ? 'Pago parcial' : 'Pendiente dentro de plazo', finalComments: comments, finishedAt: stamp(), history: [...current.order.history, { at: stamp(), event: 'Trabajo finalizado y vehículo listo para entrega' }] },
    })),
    registerPayment: ({ amount, method, reference }) => setData((current) => {
      const numericAmount = Math.min(Number(amount), orderBalance(current.order))
      if (!numericAmount || numericAmount <= 0) return current
      const payment = { id: `RC-${String(current.order.payments.length + 1).padStart(4, '0')}`, amount: numericAmount, method, reference, receivedAt: stamp(), orderId: current.order.id }
      const payments = [...current.order.payments, payment]
      const financial = payments.reduce((sum, item) => sum + item.amount, 0) >= orderTotal(current.order) ? 'Pagado' : 'Pago parcial'
      return { ...current, order: { ...current.order, payments, financial, history: [...current.order.history, { at: stamp(), event: `Pago ${payment.id} registrado en Caja` }] } }
    }),
    updatePurchase: (id, changes) => setData((current) => {
      const purchaseOrders = current.purchaseOrders.map((po) => po.id === id ? { ...po, ...changes, status: changes.acceptedAt ? 'Recibida' : changes.deliveredAt ? 'Por aceptar' : changes.sentAt ? 'En tránsito' : 'Borrador' } : po)
      const inventoryLots = current.inventoryLots.map((lot) => lot.po === id ? { ...lot, sentAt: changes.sentAt ?? lot.sentAt, deliveredAt: changes.deliveredAt ?? lot.deliveredAt, acceptedAt: changes.acceptedAt ?? lot.acceptedAt } : lot)
      return { ...current, purchaseOrders, inventoryLots }
    }),
    resetDemo: () => setData(initialJourney()),
  }), [])

  return <JourneyContext.Provider value={{ data, order: data.order, actions }}>{children}</JourneyContext.Provider>
}

export function useJourney() {
  const value = useContext(JourneyContext)
  if (!value) throw new Error('useJourney must be used inside JourneyProvider')
  return value
}

export const journeyStageOrder = ['Planificada', 'Vehículo ingresado', 'Diagnosticado', 'Presupuestado', 'Autorizado', 'En reparación', 'Entregado']
export const today = todayIso
